<script lang="ts" setup>
import { useAnnotationFormState } from "@/stores/useAnnotationFormState";
import { storeToRefs } from "pinia";
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";
import { useDocumentContainerRightPosition } from "~/composables/useDocumentContainerRightPosition";
import { useUserAnnotations } from "~/stores/useUserAnnotations";
import { EvaluativeParagraphSection } from "~/types/enums/EvaluativeParagraphSection";
import { RevisionAnnotation } from "~/types/RevisionAnnotation";

const documentId = inject("documentId") as any;
const store = useDocumentSelectionStore(documentId);

const documentParagraphStore = useDocumentRevisionParagraphsStore(documentId);

const { hasSelection, startPosition, endPosition, selectedTokenPositions } =
  storeToRefs(store);

const { generateTokenElementId } = useTokenElementIdGenerator();

const elementRef = ref();

const elementHeight = () => {
  const element = elementRef.value as HTMLElement | undefined;

  if (!element) {
    return 0;
  }

  const domRect = element.getBoundingClientRect();
  return domRect.height;
};

const windowHeightStore = useWindowHeight();
const { windowHeight } = storeToRefs(windowHeightStore);

watch(hasSelection, () => {
  if (hasSelection.value) {
    onWindowResize();
  }
});

const topCoordinates = computed(() => {
  if (!hasSelection.value || endPosition == undefined) return 0;

  const quickActionsElement = elementRef.value as HTMLElement | undefined;

  if (!quickActionsElement) return 0;

  const quickActionDomRect = quickActionsElement.getBoundingClientRect();

  if (!quickActionDomRect) return 0;

  const tokenElementId = generateTokenElementId(endPosition.value!);
  const tokenElement = document.getElementById(tokenElementId);

  if (!tokenElement) return 0;

  const domRect = tokenElement?.getBoundingClientRect();

  let top = domRect.top - (elementHeight() / 2 - domRect.height / 2);

  const documentContainerTop = useDocumentContainerTopPosition() + 16;

  top = Math.max(documentContainerTop, top);

  if (top + quickActionDomRect.height > useDocumentContainerWrapperHeight()) {
    top = useDocumentContainerWrapperHeight() - quickActionDomRect.height + 16;
  }

  return Math.max(60, top);
});

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const createAnnotation = () => {
  const commentOnlyAnnotation = createDefaultAnnotation();
  setAnnotationBeingEdited(documentId, commentOnlyAnnotation);
};

const { saveAnnotation } = useSaveAnnotation(documentId);

const addAnnotation = async (annotation: RevisionAnnotation) => {
  delete annotation.id;

  await saveAnnotation(annotation);
};

const nuxtApp = useNuxtApp();

const onQuickCommentCliked = (
  quickComment: string,
  annotation: RevisionAnnotation
) => {
  const copy = { ...annotation };

  const comments = [];

  // if (useAnnotationType(annotation.type)?.prefix) {
  //   comments.push(useAnnotationType(annotation.type)!.prefix);
  // }

  comments.push(quickComment);

  // @ts-ignore
  copy.commentObject = nuxtApp.$createQuillDelta(comments.join(" ")).ops;
  copy.quickComment = quickComment;

  addAnnotation(copy);
};

const userAnnotatinStore = useUserAnnotations();
const { quickAnnotations } = storeToRefs(userAnnotatinStore);

const leftCoordinates = ref();

const showMultipleColumns = ref(false);
const calculateButtonsHeight = () => {
  const annotations = quickAnnotations.value;
  let buttonsHeight = 0;

  if (annotations == undefined || annotations.length == 0) {
    buttonsHeight = 0;
    return;
  }

  const totalButtons = annotations.length + 3;
  buttonsHeight = totalButtons * 50;

  showMultipleColumns.value = buttonsHeight > useDocumentContainerHeight();
};
const onWindowResize = () => {
  leftCoordinates.value = useDocumentContainerRightPosition() - 40 + "px";
  calculateButtonsHeight();
};

onWindowResize();

onMounted(() => {
  window.addEventListener("resize", onWindowResize);
  onWindowResize();
});

onUnmounted(() => {
  window.removeEventListener("resize", onWindowResize);
});

const confirm = useConfirm();

const toggleIgnoreParagraphs = async () => {
  const selectedEvaluativeParagraphs =
    useDocumentSelectionStore(documentId).selectedEvaluativeParagraphs;

  const isIgnoreAll = isSelectionAllIgnored.value;

  const header = isIgnoreAll ? "Include Paragraph(s)" : "Exclude Paragraph(s)";

  const message = isIgnoreAll
    ? "Including this paragraph may cause some checklist items to reevaluate and scores to reset. Please review any scores that you have already entered."
    : "Excluding this paragraph may cause some checklist items to reevaluate and scores to reset. Please review any scores that you have already entered.";

  confirm.require({
    group: "confirm-hide-paragraph",
    header: header,
    acceptLabel: "Continue",
    rejectLabel: "Cancel",
    acceptClass: " p-2 w-[150px] bg-primary-default text-white rounded-md",
    rejectClass: " text-danger-default p-2 w-[150px] rounded-md",
    message: message,
    accept: async () => {
      await useDocumentEvaluativeParagraphsStore(
        documentId
      ).changeEvaluativeParagraphsSection(
        selectedEvaluativeParagraphs,
        isIgnoreAll
          ? EvaluativeParagraphSection.body
          : EvaluativeParagraphSection.ignore
      );

      useDocumentChecklistStore(documentId).reevaluateAllCriteria();
    },
  });
};

const documentSelectionStore = useDocumentSelectionStore(documentId);

const { selectedEvaluativeParagraphs, hasMultipleTokensSelected } = storeToRefs(
  documentSelectionStore
);

const isSelectionAllIgnored = computed(() => {
  let isAllIgnored = true;

  for (const paragraph of selectedEvaluativeParagraphs.value) {
    if (paragraph.section != EvaluativeParagraphSection.ignore) {
      isAllIgnored = false;
      break;
    }
  }

  return isAllIgnored;
});

const isMerging = ref(false);
const mergeEvaluativeParagraphs = async () => {
  isMerging.value = true;
  const selectedEvaluativeParagraphs =
    documentSelectionStore.selectedEvaluativeParagraphs;

  await useDocumentEvaluativeParagraphsStore(
    documentId
  ).mergeEvaluativeParagraphs(selectedEvaluativeParagraphs);

  useDocumentChecklistStore(documentId).reevaluateAllCriteria();

  isMerging.value = false;
};

const isSplitting = ref(false);
const splitParagraph = async () => {
  const positions = selectedTokenPositions.value;

  if (positions.length != 1) return;

  const position = positions[0];
  const selectedEvaluativeParagraphs =
    documentSelectionStore.selectedEvaluativeParagraphs;

  if (selectedEvaluativeParagraphs.length != 1) return;

  const selectedEvaluativeParagraph = selectedEvaluativeParagraphs[0];

  if (selectedEvaluativeParagraph == undefined) return;

  const token = documentParagraphStore.tokenFromGlobalPosition(position);

  if (!token) return;

  const tokenLocalPosition = token.position;

  isSplitting.value = true;

  await useDocumentEvaluativeParagraphsStore(
    documentId
  ).splitEvaluativeParagraph({
    positionToSplit: tokenLocalPosition,
    evaluativeParagraphToSplit: selectedEvaluativeParagraph,
  });

  useDocumentChecklistStore(documentId).reevaluateAllCriteria();

  isSplitting.value = false;
};
</script>

<template>
  <div
    id="quick-actions-container"
    ref="elementRef"
    class="flex flex-row flex-wrap rounded shadow bg-white absolute top-0 overflow-clip transition-opacity"
    :class="{
      invisible: !hasSelection,
      'opacity-0 pointer-events-none': !hasSelection,
      'opacity-100 z-[80] pointer-events-auto': hasSelection,
      'w-[40px]': showMultipleColumns != true,
      'w-[80px]': showMultipleColumns,
    }"
    :style="{
      top: topCoordinates + 'px',
      left: leftCoordinates,
    }"
    data-step-id="quick-actions-container"
  >
    <ConfirmDialog group="confirm-hide-paragraph" class="max-w-[500px]" />
    <DocumentQuickActionsButton class="h-[50px]" @click="createAnnotation">
      <BaseIconCommentBox />
    </DocumentQuickActionsButton>

    <DocumentQuickActionsButton
      v-for="annotation of quickAnnotations"
      :key="annotation.id"
      :annotation="annotation"
      @quick-comment-clicked="onQuickCommentCliked"
      @click="
        onQuickCommentCliked(
          useAnnotationType(annotation.type)!.inlineSymbol ?? '',
          annotation
        )
      "
    >
      <Annotation
        :annotation="annotation"
        :is-preview="true"
        class="border-dark text-dark fill-dark stroke-dark"
      />
    </DocumentQuickActionsButton>
    <DocumentQuickActionsButton
      v-tooltip.left="'Configure Your Quick Keys'"
      data-step-id="configure-quick-keys-button"
      @click="
        navigateTo('/configure/quick-keys', { open: { target: '_blank' } })
      "
    >
      <icon name="material-symbols:edit-note" />
    </DocumentQuickActionsButton>
    <DocumentQuickActionsButton
      v-tooltip.left="
        isSelectionAllIgnored ? 'Show Paragraph(s)' : 'Ignore Paragraph(s)'
      "
      @click="toggleIgnoreParagraphs"
    >
      <icon
        :name="isSelectionAllIgnored ? 'mdi:eye' : 'mdi:eye-off'"
        :class="{
          'text-primary-default': isSelectionAllIgnored,
          'text-danger-default': !isSelectionAllIgnored,
        }"
      />
    </DocumentQuickActionsButton>
    <DocumentQuickActionsButton
      v-if="hasSelection && hasMultipleTokensSelected != true"
      v-tooltip.left="'Split Paragraph'"
      :show-spinner="isSplitting"
      @click="splitParagraph"
    >
      <icon name="material-symbols:call-split" class="text-primary-default" />
    </DocumentQuickActionsButton>
    <DocumentQuickActionsButton
      v-if="selectedEvaluativeParagraphs.length > 1"
      v-tooltip.left="'Merge Paragraphs'"
      :disabled="selectedEvaluativeParagraphs.length < 2"
      @click="mergeEvaluativeParagraphs"
    >
      <icon name="material-symbols:cell-merge" class="text-primary-default" />
    </DocumentQuickActionsButton>
  </div>
</template>

<style scoped></style>
